import { computed, ref } from 'vue';
import useVuelidate from '@vuelidate/core';
import type { Dict } from '@aviata/platform-types';
import {
  controllerModel,
  dateUtils,
  searchForm as searchFormState,
  type searchFormCalendar,
  type CabinClass,
  type MappedLocation,
  type Passengers,
} from 'shared';

import { lastSearchModel } from '@/entities/last-search';

import { analyticsEventsLib } from '@/shared/lib';
import type {
  MultiTripValidationErrorCode,
  MainPageVariant,
} from '@/shared/lib';

import { useMultiTripMode } from './use-multi-trip-mode';
import { useSegmentEditor } from './use-segment-editor';
import {
  useAviaSearchSubmit,
  type AviaSearchFormEmit,
} from './use-avia-search-submit';
import { useAnimatedFlowExperiment } from './use-animated-flow-experiment';
import { MT_SESSION_KEY, SHEET_IDS } from '../config';

type AnimatedFlowField = 'dep' | 'arr' | 'dates' | 'passengers';

export type { AviaSearchFormEmit } from './use-avia-search-submit';

type MultiCityForm = ReturnType<
  typeof searchFormState.model.useMultiCitySearchForm
>;

const KNOWN_LEG0_VALIDATION_CODES: ReadonlySet<MultiTripValidationErrorCode> =
  new Set(['requiredDep', 'requiredArr', 'isSameDeparture', 'isSameArrival']);

const isKnownValidationCode = (
  validator: string | undefined,
): validator is MultiTripValidationErrorCode =>
  Boolean(validator) &&
  KNOWN_LEG0_VALIDATION_CODES.has(validator as MultiTripValidationErrorCode);

const isMultiTripSessionActive = (): boolean => {
  try {
    return sessionStorage.getItem(MT_SESSION_KEY) === '1';
  } catch {
    return false;
  }
};

/**
 * Builds the underlying multi-city form bag, restoring it from local storage
 * when a previous search exists.
 */
const buildInitialForm = (): MultiCityForm => {
  const lastSearchParams = lastSearchModel.lastSearch.getSavedSearchParams();

  if (lastSearchParams) {
    const shouldResetMultiTrip =
      !isMultiTripSessionActive() && lastSearchParams.routes.length > 1;
    const routes = shouldResetMultiTrip
      ? lastSearchParams.routes.slice(0, 1)
      : lastSearchParams.routes;

    return searchFormState.model.useMultiCitySearchForm({
      initialRoutes: routes,
      initialCabinClass: lastSearchParams.cabinClass,
      initialPassengersState: lastSearchParams.passengers,
      initialIsYouth: lastSearchParams.isYouth,
    });
  }
  return searchFormState.model.useMultiCitySearchForm({
    initialRoutes: [{ dates: [dateUtils.getTomorrowDate()] }],
  });
};

/**
 * Public composable used by `SearchForm.vue`.
 *
 * Acts as the orchestrator that wires three concerns together:
 *   1. Form state    — `searchForm` bag from `shared`.
 *   2. Mode toggle   — {@link useMultiTripMode}.
 *   3. Submit        — {@link useAviaSearchSubmit}.
 *
 * It also owns the small UI-state pieces that don't belong to any of the
 * above (which segment row is being edited, sheet ids, passenger handlers).
 */
export function useAviaSearchForm(
  getSource: () => string,
  emit: AviaSearchFormEmit,
  getVariant?: () => MainPageVariant | undefined,
) {
  // ---------- State ----------

  const searchForm = buildInitialForm();
  const selectedAirline = ref<Dict.Airlines.Airline | null>(null);
  const route = computed(() => searchForm.routes.value[0]);

  // ---------- Animated-flow experiment (separate A/B) ----------

  const animatedFlowExperiment = useAnimatedFlowExperiment();
  const animatedFlowInitialField = ref<AnimatedFlowField>('dep');

  // ---------- Validation (vuelidate, leg #1) ----------

  const validation$ = useVuelidate(
    searchFormState.lib.getCitiesValidation('main.validations'),
    route,
  );

  // ---------- Sub-modules ----------

  const bottomSheet = controllerModel.useBottomSheet();
  const modalController = controllerModel.useModal();

  const cityEditor = useSegmentEditor(searchForm);
  const calendarEditor = useSegmentEditor(searchForm);
  const calendarRoute = computed(
    () => calendarEditor.editingRoute.value ?? route.value,
  );

  const multiTrip = useMultiTripMode({
    searchForm,
    baseValidation: validation$,
  });

  // ---------- Multi-trip-aware calendar bounds ----------
  //
  // When editing leg `i` in multi-trip mode the calendar must:
  //   - disable any date earlier than leg `i-1`'s departure   (`calendarMinDate`)
  //   - disable any date later   than leg `i+1`'s departure   (`calendarMaxDate`)
  //   - mark dates already chosen on the *other* legs         (`calendarMarkedDates`)
  // All three are computed off `calendarEditor.editingIndex` so they refresh
  // as soon as the user opens a different leg's calendar.

  const calendarMinDate = computed<string | undefined>(() => {
    if (!multiTrip.isMultiTripMode.value) {
      return undefined;
    }
    const editingIdx = calendarEditor.editingIndex.value;
    if (editingIdx <= 0) {
      return undefined;
    }
    const routes = searchForm.routes.value;
    for (let i = editingIdx - 1; i >= 0; i--) {
      const date = routes[i]?.dates?.[0];
      if (date) {
        return date;
      }
    }
    return undefined;
  });

  const calendarMaxDate = computed<string | undefined>(() => {
    if (!multiTrip.isMultiTripMode.value) {
      return undefined;
    }
    const editingIdx = calendarEditor.editingIndex.value;
    const routes = searchForm.routes.value;
    for (let i = editingIdx + 1; i < routes.length; i++) {
      const date = routes[i]?.dates?.[0];
      if (date) {
        return date;
      }
    }
    return undefined;
  });

  const calendarMarkedDates = computed<string[]>(() => {
    if (!multiTrip.isMultiTripMode.value) {
      return [];
    }
    const editingIdx = calendarEditor.editingIndex.value;
    return searchForm.routes.value
      .map((legRoute, legIndex) =>
        legIndex === editingIdx ? null : legRoute.dates?.[0],
      )
      .filter((date): date is string => Boolean(date));
  });

  // ---------- Submit-time validation glue ----------

  const showError = (message: string) => {
    modalController.show(SHEET_IDS.errorModal, { errorTitle: message });
  };

  /**
   * Builds the analytics payload for `avfree_mt_submit_validation_failed` and
   * fires it. Combines vuelidate's leg-0 error codes (when the failure is on
   * the first leg) with the full extra-leg error report so PMs can see the
   * concrete validators that blocked submission.
   */
  const reportMultiTripValidationFailure = (options: {
    leg0FromVuelidate: boolean;
  }) => {
    const errorCodes: MultiTripValidationErrorCode[] = [];
    const failedLegIndexes = new Set<number>();

    if (options.leg0FromVuelidate) {
      for (const vuelidateError of validation$.value.$errors) {
        const validatorName = vuelidateError.$validator?.toString();
        const code: MultiTripValidationErrorCode = isKnownValidationCode(
          validatorName,
        )
          ? validatorName
          : 'unknown';
        errorCodes.push(code);
        failedLegIndexes.add(0);
      }
    } else {
      for (const extraError of multiTrip.collectExtraLegErrors()) {
        errorCodes.push(extraError.error_code);
        failedLegIndexes.add(extraError.leg_index);
      }
    }

    analyticsEventsLib.multiTripSubmitValidationFailed({
      error_codes: errorCodes,
      legs_count: searchForm.routes.value.length,
      failed_leg_indexes: Array.from(failedLegIndexes).sort(
        (left, right) => left - right,
      ),
      source: getSource(),
    });
  };

  /**
   * Runs vuelidate on leg #1, then the multi-trip extra-leg checker. Returns
   * the first localised error message, or `null` when both pass.
   *
   * Lives here (not in submit) because it's the single point that bridges
   * vuelidate state and multi-trip state.
   */
  const validateForSubmit = (): string | null => {
    validation$.value.$touch();
    if (validation$.value.$error) {
      multiTrip.resetExtraLegSubmitAttempted();
      const message =
        validation$.value.$errors[0]?.$message?.toString() ?? null;
      if (multiTrip.isMultiTripMode.value) {
        reportMultiTripValidationFailure({ leg0FromVuelidate: true });
      }
      return message;
    }
    const extra = multiTrip.validateForSubmit();
    if (!extra.ok) {
      reportMultiTripValidationFailure({ leg0FromVuelidate: false });
      return extra.message;
    }
    return null;
  };

  const submit = useAviaSearchSubmit({
    searchForm,
    selectedAirline,
    validate: validateForSubmit,
    showError,
    emit,
    getSource,
    getVariant,
  });

  // ---------- Sheet / calendar / city handlers ----------

  /**
   * Animated-flow gating: simple-mode leg-0 routes all four field clicks
   * (dep/arr/dates/passengers) into the unified sheet. Multi-trip mode and
   * any later legs always fall through to the existing per-field modals.
   */
  const shouldUseAnimatedFlowFor = (segmentIndex: number) =>
    animatedFlowExperiment.isAnimated.value &&
    !multiTrip.isMultiTripMode.value &&
    segmentIndex === 0;

  const showFromSheet = (segmentIndex: number) => {
    cityEditor.setEditing(segmentIndex);
    if (shouldUseAnimatedFlowFor(segmentIndex)) {
      animatedFlowInitialField.value = 'dep';
      bottomSheet.show(SHEET_IDS.animatedFlow);
      return;
    }
    bottomSheet.show(SHEET_IDS.citiesFrom);
  };
  const showToSheet = (segmentIndex: number) => {
    cityEditor.setEditing(segmentIndex);
    if (shouldUseAnimatedFlowFor(segmentIndex)) {
      animatedFlowInitialField.value = 'arr';
      bottomSheet.show(SHEET_IDS.animatedFlow);
      return;
    }
    bottomSheet.show(SHEET_IDS.citiesTo);
  };
  const updateCity = (location: MappedLocation, type: 'dep' | 'arr') => {
    cityEditor.updateEditingRoute({ [type]: location });
  };

  const openCalendar = (
    segmentIndex: number,
    mode: searchFormCalendar.config.CalendarMode,
  ) => {
    calendarEditor.setEditing(segmentIndex);
    if (shouldUseAnimatedFlowFor(segmentIndex)) {
      animatedFlowInitialField.value = 'dates';
      bottomSheet.show(SHEET_IDS.animatedFlow);
      return;
    }
    bottomSheet.show(SHEET_IDS.calendar, { mode });
  };

  const openPassengersSheet = () => {
    if (shouldUseAnimatedFlowFor(0)) {
      animatedFlowInitialField.value = 'passengers';
      bottomSheet.show(SHEET_IDS.animatedFlow);
      return;
    }
    bottomSheet.show(SHEET_IDS.passengers);
  };

  const updateDates = (dates: string[]) => {
    calendarEditor.updateEditingRoute({ dates });
  };

  const clearSecondDate = (segmentIndex: number) => {
    const routeAtSegment = searchForm.routes.value[segmentIndex];
    const firstDate = routeAtSegment?.dates?.[0];
    if (routeAtSegment && firstDate) {
      searchForm.updateRoute({ ...routeAtSegment, dates: [firstDate] });
    }
  };

  const swapCities = (segmentIndex: number) => {
    if (segmentIndex === 0) {
      analyticsEventsLib.switchCitiesClick();
    }
    const routeToSwap = searchForm.routes.value[segmentIndex];
    searchForm.updateRoute({
      ...routeToSwap,
      dep: routeToSwap.arr,
      arr: routeToSwap.dep,
    });
  };

  /**
   * Wrapper around {@link useMultiTripMode}'s `removeLeg` that detects the
   * "auto-exit" transition: removing the last extra leg drops `routes.length`
   * to 1, which collapses the form to simple mode without going through the
   * explicit toggle. We log a dedicated analytics event so this implicit
   * exit is distinguishable from the explicit toggle in funnel reports.
   */
  const removeLeg = (routeId: string) => {
    const wasMultiTripMode = multiTrip.isMultiTripMode.value;
    multiTrip.removeLeg(routeId);
    if (wasMultiTripMode && !multiTrip.isMultiTripMode.value) {
      analyticsEventsLib.multiTripAutoExit({ source: getSource() });
    }
  };

  // ---------- Passengers / cabin handlers ----------

  const updateCabinClass = (cabinClass: CabinClass) =>
    searchForm.updateCabinClass(cabinClass);
  const setIsYouth = (isYouth: boolean) => searchForm.setIsYouth(isYouth);
  const updatePassengersState = (passengers: Passengers) =>
    searchForm.updatePassengersState(passengers);

  return {
    // form state
    searchForm,
    selectedAirline,
    route,
    calendarRoute,
    calendarMinDate,
    calendarMaxDate,
    calendarMarkedDates,
    validation$,

    // multi-trip
    isMultiTripMode: multiTrip.isMultiTripMode,
    routesList: multiTrip.routesList,
    canAddMoreRoutes: multiTrip.canAddMoreRoutes,
    getDirectionValidation: multiTrip.getDirectionValidation,
    enterMultiTripMode: multiTrip.enterMultiTripMode,
    exitMultiTripMode: multiTrip.exitMultiTripMode,
    onAddAnotherLeg: multiTrip.onAddAnotherLeg,
    removeLeg,
    activateMultiTripSearch: multiTrip.activateMultiTripSearch,

    // sheets / modals
    bottomSheet,
    bottomSheetIds: SHEET_IDS,

    // editing handlers
    showFromSheet,
    showToSheet,
    openCalendar,
    openPassengersSheet,
    updateDates,
    clearSecondDate,
    updateCity,
    swapCities,

    // passengers / cabin
    updateCabinClass,
    setIsYouth,
    updatePassengersState,

    // animated-flow experiment
    isAnimatedFlow: animatedFlowExperiment.isAnimated,
    isAnimatedFlowExperimentLoading: animatedFlowExperiment.isExperimentLoading,
    animatedFlowVariant: animatedFlowExperiment.variant,
    animatedFlowInitialField,

    // submit
    submitForm: submit.submitForm,
  };
}
